/**
 * Theme Token Resolution Utilities
 *
 * resolveColor / resolveColors / isTokenPath are re-exported from
 * @launchstore/shared-puck — the single source of truth for the token
 * contract (frontend, storefront and website renderers must agree).
 * tokensToCSSVars is kept local (storefront-specific signature, used by
 * ThemeInjector).
 */

export { resolveColor, resolveColors, isTokenPath } from '@launchstore/shared-puck/theme';
import type { TokenStructure } from './validation';

/**
 * Converts theme tokens to CSS custom properties
 * @param tokens - Token structure from theme
 * @returns Object mapping CSS variable names to color values
 *
 * @example
 * tokensToCSSVars(tokens)
 * // { "--theme-brand-primary": "#000000", ... }
 */
export function tokensToCSSVars(tokens: TokenStructure | any): Record<string, string> {
  const cssVars: Record<string, string> = {};

  function traverse(obj: any, prefix: string = '') {
    for (const [key, value] of Object.entries(obj)) {
      const varName = prefix ? `${prefix}-${key}` : key;

      if (typeof value === 'string') {
        if (value.startsWith('#') || value.startsWith('rgb')) {
          cssVars[`--theme-${varName}`] = value;
        } else if (value.includes('.')) {
          // Token reference (e.g. 'brand.primary') — chain through the
          // referenced var so brand changes flow into component tokens.
          cssVars[`--theme-${varName}`] = `var(--theme-${value.replace(/\./g, '-')})`;
        } else {
          // Plain CSS keyword ('transparent', 'inherit', ...)
          cssVars[`--theme-${varName}`] = value;
        }
      } else if (typeof value === 'object' && value !== null) {
        // It's a nested object, traverse deeper
        traverse(value, varName);
      }
    }
  }

  traverse(tokens);
  return cssVars;
}
